// src/components/Register.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Link,
  styled,
  alpha,
  TextField,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  LinearProgress,
  Fade,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import previewImage from '../assets/preview.png';
import logo from '../assets/logo.png';
import AddressAutocomplete from './shared/AddressAutocomplete';
import { isWithinUploadLimit, tooLargeMessage } from '../lib/uploadLimits';

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────

// Fallback list, used only if the shared admin-managed list can't be fetched.
const BUSINESS_CATEGORIES = [
  'Cafés', 'Restaurants', 'Hair Salons', 'Nail Salons', 'Gyms',
  'Car Washes', 'Pet Groomers', 'Massage Studios', 'Beauty Bars',
  'Bakeries', 'Juice Bars', 'Barbershops', 'Other',
];

const BRAND_QUESTIONS = [
  {
    key: 'brandMeaning',
    label: 'Tell us about your business and what makes it unique.',
    placeholder: 'e.g. We\'re a family-run bakery using organic ingredients and traditional recipes you won\'t find anywhere else...',
    maxWords: 60,
  },
  {
    key: 'customerFeeling',
    label: 'How do you want customers to feel when they interact with your business?',
    placeholder: 'e.g. We want them to feel relaxed, valued, and like they\'re treating themselves...',
    maxWords: 60,
  },
  {
    key: 'customerReason',
    label: 'What are the top 3 reasons customers keep coming back?',
    placeholder: 'e.g. Consistent quality, staff who know them by name, and rewards that feel worth it...',
    maxWords: 60,
  },
  {
    key: 'customerSaying',
    label: 'If a customer could describe your business in one sentence, what would you want them to say?',
    placeholder: 'e.g. "The best coffee in town with the friendliest staff."',
    maxWords: 60,
  },
];

const TOTAL_STEPS = 6;

// ─────────────────────────────────────────────────────────────
// STYLED COMPONENTS
// ─────────────────────────────────────────────────────────────

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: '#ffffff',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const LeftPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 44%',
  padding: theme.spacing(5, 6),
  backgroundColor: '#fff',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  overflowY: 'auto',
  maxHeight: '100vh',
  [theme.breakpoints.down('md')]: {
    // With the illustration panel hidden this is the whole screen, so cap and
    // centre the form instead of stretching it across a tablet.
    flex: '1 1 auto',
    width: '100%',
    maxWidth: 520,
    margin: '0 auto',
    padding: theme.spacing(4, 3),
    maxHeight: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 2.5),
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  flex: '1 1 auto',
  backgroundColor: '#f7f8fa',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const BrandHeader = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  fontWeight: 600,
  marginBottom: theme.spacing(1),
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: 26,
  marginBottom: theme.spacing(0.5),
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: '#6b7280',
  marginBottom: theme.spacing(2.5),
  lineHeight: 1.5,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
  },
  '& .MuiInputBase-inputMultiline': {
    fontSize: 14,
  },
  '& input:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
    boxShadow: '0 0 0 1000px #fff inset !important',
  },
}));

const StyledSelect = styled(FormControl)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
  },
}));

const BlackButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: 12,
  padding: theme.spacing(1.5),
  fontWeight: 600,
  backgroundColor: '#000',
  color: '#fff',
  '&:hover': {
    backgroundColor: alpha('#000', 0.85),
  },
  '&.Mui-disabled': {
    backgroundColor: '#000',
    color: '#fff',
    opacity: 0.5,
  },
}));

const WhiteButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: 12,
  padding: theme.spacing(1.5),
  fontWeight: 600,
  backgroundColor: '#fff',
  border: '1px solid #e5e7eb',
  color: '#374151',
  '&:hover': {
    backgroundColor: '#f9fafb',
  },
}));

const HiddenInput = styled('input')({
  display: 'none',
});

const StepDot = styled(Box)(({ active, completed }) => ({
  width: active ? 28 : 8,
  height: 8,
  borderRadius: 4,
  backgroundColor: active ? '#000' : completed ? '#000' : '#e5e7eb',
  transition: 'all 0.3s ease',
}));

const WordCount = styled(Typography)(({ over }) => ({
  fontSize: 11,
  color: over ? '#ef4444' : '#9ca3af',
  textAlign: 'right',
  marginTop: 1,
  marginBottom: 0,
}));

const QuestionLabel = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  fontWeight: 500,
  color: '#374151',
  marginBottom: theme.spacing(0.5),
  lineHeight: 1.3,
}));

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

function countWords(text) {
  if (!text || !text.trim()) return 0;
  return text.trim().split(/\s+/).length;
}

// ── Opening hours ──

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Default: open Mon–Fri 9:00–17:00, closed on weekends.
const DEFAULT_HOURS = DAYS.map((day, i) => ({
  day,
  open: i < 5,
  from: '09:00',
  to: '17:00',
}));

// '17:30' -> '5:30 PM'
function to12h(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h < 12 ? 'AM' : 'PM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
}

// Turn the per-day picker into a compact readable string for storage/display,
// grouping consecutive days with identical hours, e.g.
// "Mon–Fri 9:00 AM – 5:00 PM, Sat–Sun Closed".
function serializeHours(hours) {
  const groups = [];
  hours.forEach((d) => {
    const label = d.open ? `${to12h(d.from)} – ${to12h(d.to)}` : 'Closed';
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.days.push(d.day);
    else groups.push({ label, days: [d.day] });
  });
  return groups
    .map((g) => {
      const span = g.days.length === 1 ? g.days[0] : `${g.days[0]}–${g.days[g.days.length - 1]}`;
      return `${span} ${g.label}`;
    })
    .join(', ');
}

function OpeningHoursPicker({ value, onChange, error, helperText }) {
  const setDay = (i, patch) => onChange(value.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontWeight: 500, mb: 0.75, fontSize: 13, color: '#374151' }}>Opening Hours</Typography>
      <Box sx={{ border: '1px solid', borderColor: error ? '#ef4444' : '#e5e7eb', borderRadius: 3, px: 1.5, py: 0.5 }}>
        {value.map((d, i) => (
          <Box
            key={d.day}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1,
              borderBottom: i < value.length - 1 ? '1px solid #f3f4f6' : 'none',
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ width: 40, fontWeight: 600, fontSize: 13 }}>{d.day}</Typography>
            <FormControlLabel
              sx={{ width: 92, m: 0 }}
              control={<Switch size="small" checked={d.open} onChange={(e) => setDay(i, { open: e.target.checked })} />}
              label={<Typography sx={{ fontSize: 12, color: d.open ? '#2e7d32' : '#9ca3af', fontWeight: 500 }}>{d.open ? 'Open' : 'Closed'}</Typography>}
            />
            {d.open ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  type="time"
                  size="small"
                  value={d.from}
                  onChange={(e) => setDay(i, { from: e.target.value })}
                  inputProps={{ step: 300 }}
                  sx={{ width: 128, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <Typography sx={{ color: '#9ca3af' }}>–</Typography>
                <TextField
                  type="time"
                  size="small"
                  value={d.to}
                  onChange={(e) => setDay(i, { to: e.target.value })}
                  inputProps={{ step: 300 }}
                  sx={{ width: 128, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>
            ) : (
              <Typography sx={{ fontSize: 13, color: '#bbb' }}>Closed all day</Typography>
            )}
          </Box>
        ))}
      </Box>
      {helperText && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 0.5, display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────
// REGISTER COMPONENT
// ─────────────────────────────────────────────────────────────

export default function Register() {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Step 1
  const [name, setName] = useState('');
  const [business, setBusiness] = useState('');
  const [email, setEmail] = useState('');

  // Step 2
  const [category, setCategory] = useState('');
  const [categoryOptions, setCategoryOptions] = useState(BUSINESS_CATEGORIES);
  const [subPrice, setSubPrice] = useState({ amount: 4999, currency: 'aud', interval: 'month' });

  useEffect(() => {
    fetch('/api/subscription-price')
      .then((r) => r.json())
      .then((data) => { if (data && typeof data.amount === 'number') setSubPrice(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch('/api/business-categories')
      .then((r) => r.json())
      .then((data) => {
        if (data && Array.isArray(data.categories) && data.categories.length > 0) {
          setCategoryOptions(data.categories);
        }
      })
      .catch(() => {});
  }, []);
  const [brandMeaning, setBrandMeaning] = useState('');
  const [customerFeeling, setCustomerFeeling] = useState('');
  const [customerSaying, setCustomerSaying] = useState('');
  const [customerReason, setCustomerReason] = useState('');

  // Step 3
  const [address, setAddress] = useState('');
  const [hoursByDay, setHoursByDay] = useState(DEFAULT_HOURS);
  const [website, setWebsite] = useState('');
  const [menu, setMenu] = useState(null);

  // Step 5 (verify email) + Step 6 (set password & pay)
  const [vendorId, setVendorId] = useState(null);
  const [code, setCode] = useState('');
  const [regToken, setRegToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');

  // Field errors
  const [fieldErrors, setFieldErrors] = useState({});

  const brandAnswers = { brandMeaning, customerFeeling, customerSaying, customerReason };
  const brandSetters = {
    brandMeaning: setBrandMeaning,
    customerFeeling: setCustomerFeeling,
    customerSaying: setCustomerSaying,
    customerReason: setCustomerReason,
  };

  // ── Validation ──

  const validateStep1 = () => {
    const errors = {};
    if (!name.trim()) errors.name = 'Full name is required';
    if (!business.trim()) errors.business = 'Business name is required';
    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep2 = () => {
    const errors = {};
    if (!category) errors.category = 'Please select a category';
    for (const q of BRAND_QUESTIONS.slice(0, 2)) {
      const val = brandAnswers[q.key];
      if (!val || !val.trim()) {
        errors[q.key] = 'This field is required';
      } else if (countWords(val) > q.maxWords) {
        errors[q.key] = `Please keep your response under ${q.maxWords} words`;
      }
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep3 = () => {
    const errors = {};
    for (const q of BRAND_QUESTIONS.slice(2)) {
      const val = brandAnswers[q.key];
      if (!val || !val.trim()) {
        errors[q.key] = 'This field is required';
      } else if (countWords(val) > q.maxWords) {
        errors[q.key] = `Please keep your response under ${q.maxWords} words`;
      }
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep4 = () => {
    const errors = {};
    if (!address.trim()) errors.address = 'Store address is required';
    if (!hoursByDay.some((d) => d.open)) {
      errors.openingHours = 'Please set opening hours for at least one day';
    } else if (hoursByDay.some((d) => d.open && d.from >= d.to)) {
      errors.openingHours = 'Closing time must be after opening time';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Handlers ──

  const handleNext = (e) => {
    e.preventDefault();
    setError('');
    if (step === 1 && validateStep1()) {
      setStep(2);
      setFieldErrors({});
    } else if (step === 2 && validateStep2()) {
      setStep(3);
      setFieldErrors({});
    } else if (step === 3 && validateStep3()) {
      setStep(4);
      setFieldErrors({});
    }
  };

  const handleBack = () => {
    setError('');
    setFieldErrors({});
    setStep(step - 1);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateStep4()) return;

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('name', name.trim());
      formData.append('business', business.trim());
      formData.append('email', email.toLowerCase().trim());
      formData.append('address', address.trim());
      formData.append('openingHours', serializeHours(hoursByDay));
      formData.append('website', website.trim());
      formData.append('category', category);
      formData.append('brandMeaning', brandMeaning.trim());
      formData.append('customerFeeling', customerFeeling.trim());
      formData.append('customerSaying', customerSaying.trim());
      formData.append('customerReason', customerReason.trim());

      if (menu) {
        formData.append('menu', menu);
      }

      const res = await fetch('/api/auth/register', {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.message || 'Registration failed. Please try again.');
        setIsSubmitting(false);
        return;
      }

      setVendorId(data.vendorId);
      setResendMsg('');
      setStep(5); // inline email verification
    } catch (err) {
      console.error('Registration failed:', err);
      setError('Something went wrong. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Step 5: verify the emailed 6-digit code, inline ──
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(code.trim())) {
      setFieldErrors({ code: 'Enter the 6-digit code from your email' });
      return;
    }
    setVerifying(true);
    try {
      const res = await fetch('/api/auth/verify-email-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vendorId, code: code.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setFieldErrors({ code: data.message || 'Incorrect code. Please try again.' });
        return;
      }
      setRegToken(data.token);
      setFieldErrors({});
      setStep(6); // set password & pay
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    setResendMsg('');
    setError('');
    try {
      const res = await fetch('/api/auth/resend-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vendorId }),
      });
      if (!res.ok) throw new Error();
      setResendMsg('A new code is on its way to your inbox.');
      setCode('');
    } catch {
      setError('Could not resend the code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  // ── Step 6: set the password, then go straight to Stripe Checkout ──
  const handleSetPasswordAndPay = async (e) => {
    e.preventDefault();
    setError('');
    const errs = {};
    if (!password || password.length < 8) errs.password = 'Password must be at least 8 characters';
    if (password !== confirmPassword) errs.confirmPassword = 'Passwords do not match';
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }

    setIsSubmitting(true);
    try {
      const res = await fetch(`/api/auth/complete-registration/${regToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vendorId, password }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.message || 'Could not set your password. Please try again.');
        setIsSubmitting(false);
        return;
      }

      localStorage.setItem('token', data.token);

      // Membership not required (shouldn't happen for new signups) → dashboard.
      if (!data.needsSubscription) {
        window.location.href = `/dashboard/${vendorId}`;
        return;
      }

      // Start the subscription Checkout and hand off to Stripe.
      const coRes = await fetch(`/api/vendor/${vendorId}/subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data.token}` },
        body: JSON.stringify({}),
      });
      const coData = await coRes.json();
      if (coData.alreadyActive) { window.location.href = `/dashboard/${vendorId}`; return; }
      if (!coData.ok || !coData.url) throw new Error(coData.message || 'Failed to start checkout');
      window.location.href = coData.url;
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleMenuUpload = (e) => {
    const file = e.target.files[0];
    if (file && !isWithinUploadLimit(file)) {
      setError(tooLargeMessage('Menu file'));
      return;
    }
    setMenu(file);
  };


  // ── Step Info ──

  const stepInfo = {
    1: {
      title: 'Create your account',
      subtitle: "Let's get your profile set up in just a few minutes.",
    },
    2: {
      title: 'Your brand story',
      subtitle: 'This helps us personalise your experience and connect you with the right customers.',
    },
    3: {
      title: 'Almost there!',
      subtitle: 'Just two more questions to help us understand your customers.',
    },
    4: {
      title: 'Store details',
      subtitle: 'Help customers find you with your location and hours.',
    },
    5: {
      title: 'Verify your email',
      subtitle: `We've emailed a 6-digit code to ${email || 'your inbox'}. Enter it below to continue.`,
    },
    6: {
      title: 'Set your password',
      subtitle: 'Create a password and activate your membership to finish setting up.',
    },
  };

  const progress = step <= TOTAL_STEPS ? ((step - 1) / TOTAL_STEPS) * 100 : 100;

  return (
    <Container>
      <LeftPanel>
        {/* Brand */}
        <Box component="a" href="/" sx={{ display: 'inline-block', mb: 1, textDecoration: 'none' }}>
          <Box component="img" src={logo} alt="Rewarding" sx={{ height: 32 }} />
        </Box>

        {/* Progress bar */}
        {step <= TOTAL_STEPS && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 4,
                borderRadius: 2,
                backgroundColor: '#f3f4f6',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#000',
                  borderRadius: 2,
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: 12 }}>
                Step {step} of {TOTAL_STEPS}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                {[1, 2, 3, 4, 5].map((s) => (
                  <StepDot key={s} active={s === step} completed={s < step} />
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* Title */}
        <Title>{stepInfo[step].title}</Title>
        <SubTitle>{stepInfo[step].subtitle}</SubTitle>

        {/* Error */}
        {error && (
          <Fade in>
            <Alert
              severity="error"
              onClose={() => setError('')}
              sx={{ mb: 2, borderRadius: 2 }}
            >
              {error}
            </Alert>
          </Fade>
        )}

        {/* ── Step 1: Basic Info ── */}
        {step === 1 && (
          <Fade in>
            <Box
              component="form"
              onSubmit={handleNext}
              noValidate
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <StyledTextField
                label="Full Name"
                variant="outlined"
                fullWidth
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name}
              />
              <StyledTextField
                label="Business Name"
                variant="outlined"
                fullWidth
                required
                value={business}
                onChange={(e) => setBusiness(e.target.value)}
                error={!!fieldErrors.business}
                helperText={fieldErrors.business}
              />
              <StyledTextField
                label="Email Address"
                variant="outlined"
                fullWidth
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!fieldErrors.email}
                helperText={fieldErrors.email}
              />
              <BlackButton type="submit" fullWidth sx={{ mt: 1 }}>
                Continue
              </BlackButton>

              <Box sx={{ textAlign: 'center', mt: 2.5 }}>
                <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: 13 }}>
                  Already have an account?{' '}
                  <Link href="/login" underline="hover" sx={{ color: '#000', fontWeight: 600 }}>
                    Sign in
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Fade>
        )}

        {/* ── Step 2: Business Category + Brand Q1 & Q2 ── */}
        {step === 2 && (
          <Fade in>
            <Box
              component="form"
              onSubmit={handleNext}
              noValidate
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <StyledSelect fullWidth error={!!fieldErrors.category}>
                <InputLabel id="category-label">Select your business category</InputLabel>
                <Select
                  labelId="category-label"
                  value={category}
                  label="Select your business category"
                  onChange={(e) => setCategory(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      sx: { borderRadius: 2, mt: 0.5 },
                    },
                  }}
                >
                  {categoryOptions.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
                {fieldErrors.category && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {fieldErrors.category}
                  </Typography>
                )}
              </StyledSelect>

              <Box sx={{ mt: 1 }}>
                {BRAND_QUESTIONS.slice(0, 2).map((q) => {
                  const val = brandAnswers[q.key];
                  const words = countWords(val);
                  const isOver = words > q.maxWords;
                  return (
                    <Box key={q.key} sx={{ mb: 2 }}>
                      <QuestionLabel>{q.label}</QuestionLabel>
                      <StyledTextField
                        variant="outlined"
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        placeholder={q.placeholder}
                        value={val}
                        onChange={(e) => brandSetters[q.key](e.target.value)}
                        error={!!fieldErrors[q.key] || isOver}
                        helperText={fieldErrors[q.key]}
                        sx={{ mb: 0 }}
                      />
                      <WordCount over={isOver}>
                        {words}/{q.maxWords} words
                      </WordCount>
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <WhiteButton onClick={handleBack} sx={{ flex: 1 }}>
                  Back
                </WhiteButton>
                <BlackButton type="submit" sx={{ flex: 2 }}>
                  Continue
                </BlackButton>
              </Box>
            </Box>
          </Fade>
        )}

        {/* ── Step 3: Brand Q3 & Q4 ── */}
        {step === 3 && (
          <Fade in>
            <Box
              component="form"
              onSubmit={handleNext}
              noValidate
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              {BRAND_QUESTIONS.slice(2).map((q) => {
                const val = brandAnswers[q.key];
                const words = countWords(val);
                const isOver = words > q.maxWords;
                return (
                  <Box key={q.key} sx={{ mb: 2 }}>
                    <QuestionLabel>{q.label}</QuestionLabel>
                    <StyledTextField
                      variant="outlined"
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={5}
                      placeholder={q.placeholder}
                      value={val}
                      onChange={(e) => brandSetters[q.key](e.target.value)}
                      error={!!fieldErrors[q.key] || isOver}
                      helperText={fieldErrors[q.key]}
                      sx={{ mb: 0 }}
                    />
                    <WordCount over={isOver}>
                      {words}/{q.maxWords} words
                    </WordCount>
                  </Box>
                );
              })}

              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <WhiteButton onClick={handleBack} sx={{ flex: 1 }}>
                  Back
                </WhiteButton>
                <BlackButton type="submit" sx={{ flex: 2 }}>
                  Continue
                </BlackButton>
              </Box>
            </Box>
          </Fade>
        )}

        {/* ── Step 4: Store Details ── */}
        {step === 4 && (
          <Fade in>
            <Box
              component="form"
              onSubmit={handleRegister}
              noValidate
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <AddressAutocomplete
                label="Store Address"
                value={address}
                onChange={setAddress}
                required
                error={!!fieldErrors.address}
                helperText={fieldErrors.address}
                TextFieldComponent={StyledTextField}
                variant="outlined"
              />
              <OpeningHoursPicker
                value={hoursByDay}
                onChange={setHoursByDay}
                error={!!fieldErrors.openingHours}
                helperText={fieldErrors.openingHours}
              />
              <StyledTextField
                label="Website (optional)"
                variant="outlined"
                fullWidth
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="e.g. www.yourbusiness.com"
              />

              <Box
                sx={{
                  border: '2px dashed #e5e7eb',
                  borderRadius: 3,
                  p: 2.5,
                  textAlign: 'center',
                  cursor: 'pointer',
                  mb: 2,
                  transition: 'border-color 0.2s',
                  '&:hover': {
                    borderColor: '#000',
                  },
                }}
                onClick={() => document.getElementById('menu-upload').click()}
              >
                <HiddenInput
                  accept="image/*,application/pdf"
                  id="menu-upload"
                  type="file"
                  onChange={handleMenuUpload}
                />
                {menu ? (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#000' }}>
                      {menu.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                      Click to change file
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#6b7280', mb: 0.5 }}>
                      Upload your menu (optional)
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                      PNG or JPEG, up to 10MB
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <WhiteButton onClick={handleBack} sx={{ flex: 1 }}>
                  Back
                </WhiteButton>
                <BlackButton
                  type="submit"
                  disabled={isSubmitting}
                  sx={{ flex: 2 }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={22} sx={{ color: '#fff' }} />
                  ) : (
                    'Continue'
                  )}
                </BlackButton>
              </Box>
            </Box>
          </Fade>
        )}

        {/* ── Step 5: Verify email (inline code) ── */}
        {step === 5 && (
          <Fade in>
            <Box
              component="form"
              onSubmit={handleVerifyCode}
              noValidate
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <StyledTextField
                label="6-digit code"
                variant="outlined"
                fullWidth
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                error={!!fieldErrors.code}
                helperText={fieldErrors.code}
                placeholder="123456"
                inputProps={{ inputMode: 'numeric', maxLength: 6, style: { letterSpacing: 8, fontSize: 20, fontWeight: 600 } }}
              />

              {resendMsg && (
                <Typography variant="body2" sx={{ color: '#16a34a', mb: 1 }}>{resendMsg}</Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>Didn't get it?</Typography>
                <Link
                  component="button"
                  type="button"
                  onClick={handleResendCode}
                  disabled={resending}
                  sx={{ fontWeight: 600, color: '#000', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  {resending ? 'Resending…' : 'Resend code'}
                </Link>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <WhiteButton onClick={handleBack} sx={{ flex: 1 }}>Back</WhiteButton>
                <BlackButton type="submit" disabled={verifying} sx={{ flex: 2 }}>
                  {verifying ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Verify email'}
                </BlackButton>
              </Box>
            </Box>
          </Fade>
        )}

        {/* ── Step 6: Set password & pay ── */}
        {step === 6 && (
          <Fade in>
            <Box
              component="form"
              onSubmit={handleSetPasswordAndPay}
              noValidate
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: '#16a34a' }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Email verified</Typography>
              </Box>

              <StyledTextField
                label="Password"
                type="password"
                variant="outlined"
                fullWidth
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!fieldErrors.password}
                helperText={fieldErrors.password || 'At least 8 characters'}
              />
              <StyledTextField
                label="Confirm password"
                type="password"
                variant="outlined"
                fullWidth
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={!!fieldErrors.confirmPassword}
                helperText={fieldErrors.confirmPassword}
              />

              <Box sx={{ border: '1px solid #eee', borderRadius: 3, p: 2, mb: 2, bgcolor: '#fafafa' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>Rewarding Membership</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 800, color: '#1a1a2e' }}>${(subPrice.amount / 100).toFixed(2)}<Typography component="span" variant="caption" sx={{ color: '#9ca3af', fontWeight: 600 }}> {subPrice.currency.toUpperCase()} / {subPrice.interval}</Typography></Typography>
                </Box>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                  You'll be taken to secure Stripe checkout to activate your account. Cancel anytime.
                </Typography>
              </Box>

              <BlackButton type="submit" disabled={isSubmitting} fullWidth>
                {isSubmitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Set password & subscribe'}
              </BlackButton>
            </Box>
          </Fade>
        )}
      </LeftPanel>

      {/* RIGHT PANEL */}
      <RightPanel sx={{ display: 'flex', flexDirection: 'column', rowGap: 4 }}>
        <Box sx={{ marginRight: '80%', display: 'flex', columnGap: 0.8 }}>
          <Box sx={{ borderRadius: '100%', width: 10, height: 10, backgroundColor: '#FF5F57' }} />
          <Box sx={{ borderRadius: '100%', width: 10, height: 10, backgroundColor: '#FFBD2E' }} />
          <Box sx={{ borderRadius: '100%', width: 10, height: 10, backgroundColor: '#27C93F' }} />
        </Box>

        <Box
          component="img"
          src={previewImage}
          alt="Website Preview"
          sx={{
            width: '130%',
            height: 'auto',
            marginRight: '-45%',
          }}
        />
      </RightPanel>
    </Container>
  );
}
